<template>
  <div class="voucher" v-if="canRender">
    <div class="voucher__header" :class="handleBackgroudHeader">
      <Transition mode="out-in">
        <vc-voucher-contabil-header-timer
          @close="handleClose"
          :timer="expireLimit"
          @click="handleShowBody"
          v-if="!showBody"
        />
        <vc-voucher-contabil-header-opened
          v-else
          @action="handleAction"
          :icon="handleIcone"
          :label="handleOpenedLabel"
          @click="handleShowBody"
        >
        </vc-voucher-contabil-header-opened>
      </Transition>
    </div>
    <div class="voucher__body" :class="handleVoucherClass">
      <Transition mode="out-in">
        <div v-if="!isCompleted">
          <div class="voucher__body-oferta">
            <label class="voucher__body-oferta__title"
              >Gratuito por tempo limitado:
              <label class="voucher__body-oferta__timer"
                ><vc-material-icon
                  class="voucher__body-oferta__timer-icon"
                  icon="alarm"
                  style="font-size: 16px"
                ></vc-material-icon>
                {{ expireLimit }}</label
              >
            </label>
          </div>

          <div class="vouher__body-info">
            <vc-voucher-contabil-body-info
              :isLoading.camel="isLoadingForm"
              @handleFormSubmitted="handleForm"
            />
          </div>
        </div>
        <div v-else>
          <div class="voucher__body-completed">
            <div class="voucher__body-completed__img">
              <img :src="Diagnostico" />
            </div>

            <div class="voucher__body-completed__desc">
              <h1>Você receberá sua análise em breve</h1>
              <span>Caso não receba, verifique a caixa de spam</span>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineCustomElement, onMounted, ref, watch } from "vue";
import VoucherContabilHeaderTimerCe from "./VoucherContabilHeaderTimer.ce.vue";
import VoucherContabilHeaderOpenedCe from "./VoucherContabilHeaderOpened.ce.vue";
import VoucherContabilBodyInfo from "./VoucherContabilBodyInfo.ce.vue";
import Diagnostico from "./diagnostico.png";
import { F360UnitDate } from "@/core/plugins/date/F360Date";
import useVoucherTimer from "./useVoucherTimer";
import useVoucherForm, { Form } from "./useVoucherForm";
import { diContainer } from "@/inversify.config";
import { IF360Validator } from "@/core/validators/F360Validator";
import { DIValidators } from "@/core/@types/types";
import {
  IBuscarVoucherUseCase,
  IBuscarVoucherUseCaseOutput,
  buscarVoucherUseCaseDI
} from "../ports/ListarVoucherUsecase";
import {
  IIniciarVoucherUseCase,
  iniciarVoucherUseCaseDI
} from "../ports/IniciarVoucherUsecase";
import useNotification from "@/composables/shared/notification/useNotification";
import {
  IDesativarVoucherUseCase,
  desativarVoucherUseCaseDI
} from "../ports/DesativarVoucherUseCase";
import useBreakpointsElement from "@/core/composables/breakpoints/useBreakpointElement";

customElements.get("vc-voucher-contabil-header-timer") ||
  customElements.define(
    "vc-voucher-contabil-header-timer",
    defineCustomElement(VoucherContabilHeaderTimerCe)
  );
customElements.get("vc-voucher-contabil-header-opened") ||
  customElements.define(
    "vc-voucher-contabil-header-opened",
    defineCustomElement(VoucherContabilHeaderOpenedCe)
  );
customElements.get("vc-voucher-contabil-body-info") ||
  customElements.define(
    "vc-voucher-contabil-body-info",
    defineCustomElement(VoucherContabilBodyInfo)
  );

const { breakpoints } = useBreakpointsElement();
const { expireLimit, timer, date, startTime, horaFutura } = useVoucherTimer();
const { handleFormSubmit, isLoadingForm } = useVoucherForm();
const notification = useNotification();
const isCielo = diContainer.get<IF360Validator>(DIValidators.CIELO);
const temAcessoContabil = diContainer.get<IF360Validator>(
  DIValidators.ACESSO_CONTABIL
);
const sysAdmin = diContainer.get<IF360Validator>(DIValidators.SYS_ADMIN);
const getVoucherUsecase = diContainer.get<IBuscarVoucherUseCase>(
  buscarVoucherUseCaseDI
);
const iniciarVoucherUseCase = diContainer.get<IIniciarVoucherUseCase>(
  iniciarVoucherUseCaseDI
);
const desativarVoucherUsecase = diContainer.get<IDesativarVoucherUseCase>(
  desativarVoucherUseCaseDI
);

const showBody = ref<null | boolean>(true);
const canRender = ref(false);
const isCompleted = ref(false);

const voucherId = ref<string | undefined>();

const startInterval = () => {
  const oneSecond = 1000;
  interval = setInterval(() => {
    timer.value = date.add(timer.value, 1, F360UnitDate.Second);
  }, oneSecond);
};

let interval: undefined | ReturnType<typeof setInterval> = undefined;

const cannotRenderVoucher = () => {
  return (
    isCielo.handleValidation() ||
    temAcessoContabil.handleValidation() ||
    sysAdmin.handleValidation() ||
    breakpoints.isSmallerOrEqual("sm")
  );
};

const handleUnstartedVoucher = async (
  response: IBuscarVoucherUseCaseOutput
) => {
  const responseStartVoucher = await iniciarVoucherUseCase.sendDataVisualizacao(
    response.id,
    date.now().toISOString()
  );

  if (responseStartVoucher.isFailure) return (canRender.value = false);

  startInterval();
  canRender.value = true;
};

const isOverTime = computed(() => date.isBefore(horaFutura.value, date.now()));

const handleStartVoucher = async (response: IBuscarVoucherUseCaseOutput) => {
  if (typeof response.start !== "string")
    return await handleUnstartedVoucher(response);

  startTime.value = new Date(response.start);

  if (isOverTime.value) return (canRender.value = false);

  canRender.value = true;

  startInterval();
};

onMounted(async () => {
  if (cannotRenderVoucher()) return (canRender.value = false);
  const response = await getVoucherUsecase.getVoucher();

  if (response.isFailure) return (canRender.value = false);

  voucherId.value = response.getValue().id;

  handleStartVoucher(response.getValue());
});

const handleAction = () => {
  if (!isCompleted.value) return;

  canRender.value = false;
};

const handleForm = async (params: { detail: Form[] }) => {
  if (voucherId.value === undefined)
    return notification.showNotification(
      "Ocorreu um erro inesperado, por favor, entre em contato com o time de suporte",
      { type: "error" }
    );

  const value = params.detail.at(0);

  if (!value?.email || !value.whats)
    return notification.showNotification(
      "E-mail ou telefone não preenchidos corretamente",
      { type: "error" }
    );

  const result = await handleFormSubmit({
    email: value.email,
    voucherId: voucherId.value,
    whats: value.whats
  });

  if (result?.isFailure)
    return notification.showNotification(result.error, { type: "error" });

  isCompleted.value = true;
};

const handleShowBody = () => {
  if (isCompleted.value) return (showBody.value = true);
  showBody.value = !showBody.value;
};

const handleClose = () => {
  canRender.value = false;

  if (!voucherId.value || isCompleted.value) return;

  desativarVoucherUsecase.desativarVoucher(voucherId.value);
};

const handleVoucherClass = computed(() => {
  if (showBody.value === null) return "voucher__body";

  return showBody.value ? "voucher__body--open" : "voucher__body--close";
});

watch(
  () => timer.value,
  () => {
    if (isOverTime.value) {
      clearInterval(interval);
      canRender.value = false;
    }
  }
);

const handleOpenedLabel = computed(() =>
  isCompleted.value
    ? "Cadastro enviado!"
    : "Seu diagnóstico contábil por IA - grátis."
);
const handleBackgroudHeader = computed(() =>
  showBody.value ? "voucher__header--opened" : "voucher__header--closed"
);
const handleIcone = computed(() =>
  isCompleted.value ? "close_small" : "expand_more"
);
</script>

<style lang="scss">
$voucher-height: 64vh;
$voucher-width: 32rem;

@keyframes slideup {
  from {
    height: 0;
  }

  to {
    height: $voucher-height;
  }
}

@keyframes slidedown {
  from {
    height: $voucher-height;
  }

  to {
    height: 0;
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease-in;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.voucher {
  position: fixed;
  bottom: 52px;
  z-index: 2;
  font-family: "Plus Jakarta Sans";
  box-shadow: 6px 6px 6px 0px #0000000f;
  width: $voucher-width;
  background-color: #fff;
  border-radius: 5px;
  border-color: #e7e7e7;

  &__header {
    height: 5vh;
    border-radius: 5px 5px 0px 0px;
    padding: 16px;

    &--opened {
      background: #d7ffea;
    }

    &--closed {
      background: linear-gradient(90deg, #00cc66 0%, #bef4d8 100%);
    }
  }

  .voucher__body {
    background-color: #fff;
    cursor: default;
    height: 0;

    &--open {
      animation: slideup 1s forwards;
      display: inline;
    }

    &--close {
      animation: slidedown 1s forwards;
    }

    &-completed {
      padding-top: 24px;
      align-content: center;

      &__img {
        display: flex;
        align-items: start;
        justify-content: center;
      }

      &__desc {
        padding: 24px 24px 48px 24px;
        display: flex;
        flex-direction: column;
        align-items: center;

        h1 {
          font-size: 24px;
          line-height: 30px;
          font-weight: 700;
          color: #2e2e2e;
          text-align: center;
          margin: 0;
          margin-bottom: 16px;
          width: 398px;
        }

        span {
          color: #5d5d5d;
          font-size: 12px;
          line-height: 16px;
          text-align: center;
          font-weight: 300;
          display: block;
        }
      }
    }

    &-oferta {
      width: 100%;
      height: 24px;
      padding-top: 8px;
      text-align: center;
      margin-bottom: 24px;

      &__title {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #4f4f4f;
        font-size: 12px;
        line-height: 16px;
        font-weight: 600;
      }

      &__timer {
        border-radius: 5px;
        padding: 4px;
        height: 100%;
        background-color: #fffec1;
        color: #a14e0b;
        display: flex;
        width: 56px;
        margin-left: 4px;
        gap: 4px;

        &-icon {
          font-size: 16px;
        }
      }
    }
  }
}
</style>
